<template>
  <BaseModal
    :modalTitle="$t('area.change-account.title')"
    :modalIntro="$t('area.change-account.intro')">
    <!-- Current account -->
    <div class="mb-2">
      <span class="text-sm text-text-secondary">
        {{ $t('area.change-account.current-account') }}
      </span>
      <span class="font-bold">{{ labAccount?.name }}</span>
    </div>

    <!-- All lab accounts -->
    <UiTabNav
      class="mb-2"
      :navItems="navItems"
      @nav-item-click="onNavItemClick" />

    <div>
      <div
        v-for="account in currentAccounts"
        :key="account.id"
        class="mb-2 cursor-pointer rounded bg-surface-muted p-3 hover:bg-surface-sunken"
        :class="{
          'pointer-events-none text-text-muted': account.id === labAccount?.id,
        }"
        @click="onAccountClick(account)">
        <span>{{ account.name }}</span>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import type { LabAccount } from '@team-milva/lab-models/labAccount'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'
import type { ITabNavOption } from '@team-milva/lab-components'
import { mapActions, mapState } from 'pinia'

export default {
  name: 'ChangeAccountModal',
  emits: ['close'],
  data() {
    return {
      activeTab: 'active-accounts' as
        | 'active-accounts'
        | 'deactivated-accounts',
    }
  },
  computed: {
    ...mapState(useAccountStore, ['labAccount', 'allLabAccounts']),
    accountGroups(): { active: LabAccount[]; deactivated: LabAccount[] } {
      return this.allLabAccounts.reduce(
        (acc, account) => {
          if (account.activeState === 'active') {
            acc.active.push(account)
          } else if (account.activeState === 'inactive') {
            acc.deactivated.push(account)
          }
          return acc
        },
        { active: [] as LabAccount[], deactivated: [] as LabAccount[] }
      )
    },
    navItems() {
      const items: ITabNavOption[] = []
      const { active, deactivated } = this.accountGroups

      if (!deactivated.length) {
        return items
      }

      // Active accounts
      items.push({
        id: 'active-accounts',
        title: `${this.$t('state.active-plural')} (${active.length})`,
        isActive: this.activeTab === 'active-accounts',
      })

      items.push({
        id: 'deactivated-accounts',
        title: `${this.$t('state.deactivated-plural')} (${deactivated.length})`,
        isActive: this.activeTab === 'deactivated-accounts',
      })

      return items
    },
    currentAccounts() {
      return this.activeTab === 'active-accounts'
        ? this.accountGroups.active
        : this.accountGroups.deactivated
    },
  },
  methods: {
    ...mapActions(useUserStore, ['setImpersonationNone']),
    onNavItemClick(id: string) {
      this.activeTab = id as 'active-accounts' | 'deactivated-accounts'
    },
    onAccountClick(account: LabAccount) {
      if (account.id === this.labAccount?.id) {
        return
      }

      // An impersonated employee belongs to one specific lab — always exit
      // impersonation when switching labs (also enforced defensively in
      // accountStore.getLabAccountFromSlug for switches not made via this
      // modal, e.g. a direct URL change).
      this.setImpersonationNone()

      this.$router.push({ name: 'App', params: { labSlug: account.subDomain } })
      this.$emit('close')
    },
  },
}
</script>

